<template>
  <health-forecast-teaser
    v-if="teaser.type === 'BUTTONTEASER'"
    class="teaser"
    :teaser="teaser"
  />
  <a
    v-else
    class="magazine-teaser"
    :class="{ [`magazine-teaser--width-${teaser.width}`]: true }"
    :href="teaser.url"
    data-track-as="teaser"
    :data-track-text="teaser.title"
  >
    <div class="magazine-teaser__container">
      <div v-if="teaser.image" class="magazine-teaser__picture">
        <div class="magazine-teaser__image-container">
          <picture>
            <source
              v-for="(source, i) in imageSources"
              :key="i"
              :media="source.media"
              :srcset="source.srcset"
              :sizes="source.sizes"
            />
            <img
              :src="teaser.image"
              data-san-object-fit="cover"
              class="magazine-teaser__image"
            />
          </picture>
        </div>
      </div>
      <div class="magazine-teaser__wrapper">
        <div class="magazine-teaser__content">
          <div v-if="isDossier" class="magazine-teaser__tag">
            <div class="magazine-teaser__tag-title" v-text="teaser.tag" />
            <div
              class="magazine-teaser__tag-count"
              v-text="$t('articleCount', { count: teaser.articleCount })"
            />
          </div>
          <span
            v-else
            class="magazine-teaser__content-title"
            v-html="teaser.title"
          />
          <p class="magazine-teaser__text">{{ teaser.text }}</p>
        </div>
        <div v-if="mediaLinks.length" class="magazine-teaser__media">
          <div
            v-for="link of mediaLinks"
            :key="link.label"
            class="magazine-teaser__media-icon"
          >
            <dotcom-icon :name="link.icon" art="light" />
            <span class="magazine-teaser__label" v-text="$t(link.label)" />
          </div>
        </div>
      </div>
    </div>
  </a>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

import HealthForecastTeaser from '../../healthForecast/common/HealthForecastTeaser'

const AVAILABLE_MEDIA_TYPES = [
  'video',
  'audio',
  'gallery',
  'infographic',
  'survey',
]

export default {
  name: 'SanitasMagazineTeaser',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    HealthForecastTeaser,
  },
  props: {
    teaser: {
      type: Object,
      default: () => {},
    },
    language: {
      type: String,
      default: 'de',
    },
  },
  setup(props) {
    const loadLabels = lang => import(`../i18n/${lang}.json`)
    provideI36n(props.language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data() {
    return {
      mediaLinks: [],
    }
  },
  computed: {
    imageSources() {
      if (this.teaser.imageSources && this.teaser.width) {
        let mapper = {
          1: { key: 'tall', sizes: '(min-width: 1200px) 350px, 33vw' },
          2: { key: 'wide', sizes: '(min-width: 1200px) 540px, 50vw' },
          3: { key: 'extrawide', sizes: '(min-width: 1200px) 725px, 66vw' },
          4: { key: 'extrawide', sizes: '(min-width: 1200px) 1100px, 100vw' },
        }
        let sources = []
        if (mapper[this.teaser.width]) {
          sources.push({
            media: '(min-width: 860px)',
            srcset: this.teaser.imageSources[mapper[this.teaser.width].key],
            sizes: this.teaser.imageSources[mapper[this.teaser.width].sizes],
          })
        }
        sources.push({
          media: '(max-width: 860px) and (min-width: 490px)',
          srcset: this.teaser.imageSources.regular,
          sizes: '50vw',
        })
        sources.push({
          media: '(max-width: 490px)',
          srcset: this.teaser.imageSources.wide,
          sizes: '100vw',
        })
        return sources
      }
      return undefined
    },
    isDossier() {
      return this.teaser.tag && this.teaser.articleCount
    },
  },
  mounted() {
    this.mediaLinks = this.getActiveMediaConfiguration()
  },
  methods: {
    getActiveMediaConfiguration() {
      /*
        A standard config based on e.g. the type 'video' will generate:
        - check string: addVideoIcon
        - icon: icon_video
        - label: videoLabel
        'infographic' and 'survey' have special icon names though.
      */
      return AVAILABLE_MEDIA_TYPES.reduce((acc, type) => {
        const typeCheckString = `add${
          type.charAt(0).toUpperCase() + type.slice(1)
        }Icon`
        if (this.teaser[typeCheckString]) {
          const icon = this.mapIcon(type)
          const label = `${type}Label`
          return acc.concat({ icon, label })
        }
        return acc
      }, [])
    },
    mapIcon(type) {
      switch (type) {
        case 'video':
          return 'circle-play'
        case 'audio':
          return 'volume-high'
        case 'gallery':
          return 'images'
        case 'infographic':
          return 'message-image'
        case 'survey':
          return 'square-poll-vertical'
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.magazine-teaser {
  @mixin teaserHoverZoom .magazine-teaser__image-container
    .magazine-teaser__image;

  text-decoration: none;
  width: 100%;
  padding: 6px 0;

  &:hover {
    text-decoration: none;
    border-bottom: none;
    color: var(--c-primary-neutral-1);
  }
}

.magazine-teaser {
  &--width-1,
  &--width-2,
  &--width-3,
  &--width-4 {
    .magazine-teaser__image-container {
      padding-top: 50%;
    }
  }
}

.magazine-teaser__wrapper {
  background: var(--c-primary-neutral-3);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  padding-bottom: 20px;
}

.magazine-teaser__container {
  transition: 0.3s;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
  min-height: 0;
}

.magazine-teaser__image-container {
  width: 100%;
  flex-grow: 0;
  position: relative;
  overflow: hidden;
  padding-top: 74.42529%;
}

.magazine-teaser__image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: cover;
}

.magazine-teaser__content {
  border-top: none;
  color: var(--c-primary-neutral-1);
  padding: 10px 20px;
  text-align: center;

  /*
    Needs to be that specific because of .magazine-article .content-area p
  */

  .magazine-teaser__text {
    font-family: 'Open Sans', sans-serif;
    color: var(--c-primary-neutral-1);
    padding-top: 10px;
    margin-bottom: 0;
    font-size: 15px;
    line-height: 20px;
    overflow-wrap: break-word;
    display: none;
  }
}

.magazine-teaser__content-title {
  padding: 4px 0 0;
  font-weight: bold;
  color: var(--c-primary-neutral-1);
  font-family: 'Faustina', 'The Mix', sans-serif;
  letter-spacing: 0.2px;
  font-size: 20px;
  line-height: 20px;
}

.magazine-teaser__tag {
  padding: 0 20px;
  background: var(--c-primary-neutral-3);
  font-weight: bold;
  font-size: 11px;
  color: var(--c-primary-color-2);
  line-height: 15px;
  text-align: center;
}

.magazine-teaser__tag-title {
  font-weight: bold;
  font-family: 'Faustina', 'The Mix', sans-serif;
  font-size: 20px;
  line-height: 20px;
}

.magazine-teaser__tag-count {
  white-space: nowrap;
  padding-top: 5px;
}

.magazine-teaser__media {
  padding: 5px 0 10px 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.magazine-teaser__media-icon {
  display: flex;
  padding: 5px 8px 0 0;
  color: var(--c-primary-neutral-1);
}

.magazine-teaser__label {
  font-family: 'Faustina', serif;
  padding: 1px 0 0 3px;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.2px;
  color: var(--c-primary-neutral-1);
}

@media (--xs) {
  .magazine-teaser {
    width: calc((100% - 20px) / 2);
    padding: 0 0 10px;
  }

  .magazine-teaser__container {
    min-height: calc((100vw - 860px) * 0.5 + 440px);
  }

  .magazine-teaser {
    &--width-1,
    &--width-2,
    &--width-3,
    &--width-4 {
      .magazine-teaser__image-container {
        padding-top: 70.27%;
      }
    }
  }

  .magazine-teaser__content-title {
    line-height: 20px;
  }

  .magazine-teaser__content .magazine-teaser__text {
    display: block;
  }
}

@media (min-width: 860px) {
  .magazine-teaser {
    width: auto;

    &--width-1 {
      width: calc((100% - 40px) / 3);
    }

    &--width-2 {
      width: calc((100% - 20px) / 2);
    }

    &--width-3 {
      width: calc((100% - 10px) / 3 * 2);
    }

    &--width-4 {
      width: 100%;
    }
  }

  .magazine-teaser__container {
    min-height: calc((100vw - 1200px) * 0.25 + 440px);
  }

  .magazine-teaser__container {
    height: 100%;
  }

  .magazine-teaser {
    &--width-1 .magazine-teaser__image-container {
      padding-top: 74.42529%;
    }

    &--width-2 .magazine-teaser__image-container {
      padding-top: 48.366%;
    }

    &--width-3 .magazine-teaser__image-container {
      padding-top: 35.82296%;
    }

    &--width-4 .magazine-teaser__image-container {
      padding-top: 29.09%;
    }
  }
}

@media (--xl) {
  .magazine-teaser__container {
    min-height: auto;
  }
}
</style>
